import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/class.util';
import { handleInputCompositionStart } from '@/shared/lib/input.util';

export type LabeledInputStatus = '' | 'focus' | 'error';

const labeledInputVariants = cva(
  'main:rounded-md rounded-[8px] border main:py-[17px] main:pl-[20px] main:pr-[14px] py-[10px] px-[10px] flex items-center focus:outline-hidden focus:ring-0 placeholder:text-gray-300 text-body2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 transition-all text-gray-600 focus:border-primary focus-visible:border-primary focus-visible:outline-hidden focus-visible:ring-0 bg-[#FAFCFF]',
  {
    variants: {
      variant: {
        default: '',
      },
      inputSize: {
        full: 'w-full',
        inline: 'inline-flex',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'full',
    },
  }
);

export interface LabeledInputProps
  extends
    React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof labeledInputVariants> {
  label: string;
  value: string | number;
  onChangeValue: React.Dispatch<React.SetStateAction<string>>;
  button?: React.ReactNode;
  status?: LabeledInputStatus;
  msg?: string;
  required?: boolean;
  requiredText?: string;
  placeholder?: string;
  containerClassName?: string;
  labelClassName?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  englishOnly?: boolean;
}

const LabeledInput = React.forwardRef<HTMLInputElement, LabeledInputProps>(
  (
    {
      className,
      labelClassName,
      value,
      onChangeValue,
      containerClassName,
      type,
      variant,
      inputSize,
      status,
      id,
      label,
      required,
      requiredText,
      button,
      placeholder,
      msg,
      onKeyDown,
      englishOnly,
      ...props
    },
    ref
  ) => {
    const shouldResetIME = englishOnly || type === 'password';
    const handleEnglishOnlyBeforeInput = (
      e: React.FormEvent<HTMLInputElement>
    ) => {
      const data = (e.nativeEvent as InputEvent).data;
      if (data !== null && /[^\x20-\x7E]/.test(data)) {
        e.preventDefault();
      }
    };

    const inputStyleByStatus =
      status === 'error' ? 'border-danger' : 'border-gray-100';
    const msgStyleByStatus =
      status === 'error'
        ? 'text-danger font-[400] text-caption1'
        : 'text-primary-dark font-[400] text-caption1';

    const divStyleBySize =
      inputSize === 'inline'
        ? 'inline-flex relative main:mb-[8px]'
        : 'w-full relative flex main:mb-[8px]';
    return (
      <div className={cn('w-full', containerClassName)}>
        <label
          htmlFor={id}
          className={cn(
            'text-label2 main:mb-[12px] main:text-label1 mb-[8px] block font-[400] text-gray-800',
            labelClassName
          )}
        >
          {label}
          {required ? (
            <span
              className={`text-danger ${requiredText ? 'ml-[4px] text-[10px] font-[700]' : 'font-[500]'}`}
            >
              {requiredText || '*'}
            </span>
          ) : (
            ''
          )}
        </label>
        <div className={divStyleBySize}>
          <input
            id={id}
            type={type}
            className={cn(
              labeledInputVariants({
                variant,
                inputSize,
                className,
              }),
              inputStyleByStatus
            )}
            placeholder={placeholder}
            ref={ref}
            value={value}
            onBeforeInput={
              englishOnly ? handleEnglishOnlyBeforeInput : undefined
            }
            onCompositionStart={
              shouldResetIME ? handleInputCompositionStart : undefined
            }
            onChange={(e) => {
              const nextValue = englishOnly
                ? e.target.value.replace(/[^\x20-\x7E]/g, '')
                : e.target.value;
              onChangeValue(nextValue);
            }}
            onKeyDown={onKeyDown}
            {...props}
          />
          {button && (
            <div className="main:right-[8px] absolute top-1/2 right-0 flex translate-y-[-50%] items-center">
              {button}
            </div>
          )}
        </div>

        <p
          className={`text-caption1 main:ml-[20px] main:text-label2 ml-[8px] h-[20px] ${msgStyleByStatus}`}
        >
          {msg}
        </p>
      </div>
    );
  }
);
LabeledInput.displayName = 'Input';

export { LabeledInput };
